import {
	DownloadOutlined,
	EllipsisOutlined,
	ExportOutlined,
	LinkOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, MenuProps } from "antd";
import { CSSProperties, useState } from "react";
import { Link } from "react-router-dom";
import { alertMessage } from "../../../utils/alert";
import { ReportItemKind } from "../../../utils/reporting/reportItemKinds";
import { StreamItemsSummary } from "../../../utils/reporting/reportStream";
import {
	getReportStatusKey,
	getReportStatusLabel,
	ReportStatusKey,
} from "../../../utils/reporting/reportStatusOptions";
import { getReportStatusColor } from "../../../utils/reporting/statusDisplay";
import { TenantLogo } from "../TenantLogo";

// A status's colour, for the stream's styles (reportStream.scss).
export const getStatusColorStyle = (
	key: ReportStatusKey,
	isRejected?: boolean
) =>
	({
		"--report-stream-status": getReportStatusColor(key, isRejected),
	}) as CSSProperties;

// The report's number, as the link the whole row opens (the styles stretch it
// over the row), and the report's operator.
export function ReportStreamNumberCell({
	to,
	reportNumber,
	operator,
}: Readonly<{ to: string; reportNumber?: unknown; operator?: string }>) {
	return (
		<>
			<Link className="report-stream__link" to={to}>
				{typeof reportNumber === "string" && reportNumber !== ""
					? reportNumber
					: "—"}
			</Link>
			{operator && (
				<span className="report-stream__operator">
					<TenantLogo tenantKey={operator} size={20} showName />
				</span>
			)}
		</>
	);
}

// A report's title, clamped to two lines.
export function ReportStreamTitle({ title }: Readonly<{ title?: unknown }>) {
	return (
		<span className="report-stream__title-text">
			{typeof title === "string" && title.trim() !== "" ? title : "—"}
		</span>
	);
}

// The line under a report's title: a tag, such as the report's type, then
// each part once, after a "·". Where the line wraps, the "·" that would start
// a line is hidden (reportStream.scss). Nothing without a tag or a part.
export function ReportStreamMeta({
	tag,
	parts,
}: Readonly<{ tag?: string; parts: readonly string[] }>) {
	const shownParts = [...new Set(parts)];
	if (!tag && shownParts.length === 0) return null;
	return (
		<span className="report-stream__meta">
			<span className="report-stream__meta-parts">
				{tag ? <span className="report-stream__tag">{tag}</span> : null}
				{shownParts.map((part) => (
					<span key={part} className="report-stream__meta-part">
						<span className="report-stream__meta-dot" aria-hidden="true">
							·
						</span>
						<span>{part}</span>
					</span>
				))}
			</span>
		</span>
	);
}

// A report's status, named as the status figures name it. A report closed as
// rejected is still "Closed", in the rejected colour.
export function ReportStreamStatusChip({
	status,
	openStatus,
	isRejected,
}: Readonly<{ status?: string; openStatus?: string; isRejected?: boolean }>) {
	const key = getReportStatusKey(status, openStatus);
	if (!key) return null;
	return (
		<span
			className="report-stream__status"
			style={getStatusColorStyle(key, isRejected)}
		>
			<span className="report-stream__dot" aria-hidden="true" />
			{getReportStatusLabel(key)}
		</span>
	);
}

// A report's findings or actions, summed up (getItemsSummary).
export function ReportStreamItemsSummary({
	summary,
	kind,
}: Readonly<{ summary: StreamItemsSummary; kind: ReportItemKind }>) {
	const { total } = summary;
	if (total === 0) {
		return <span className="report-stream__muted">{`No ${kind.plural}`}</span>;
	}
	if (summary.kind === "count") {
		return (
			<span className="report-stream__items-text">
				{`${total} ${total === 1 ? kind.noun : kind.plural}`}
			</span>
		);
	}
	const share = (count: number) => `${(count / total) * 100}%`;
	return (
		<>
			<span className="report-stream__items-text">
				{`${summary.completed} of ${total} completed`}
			</span>
			<span className="report-stream__bar" aria-hidden="true">
				<span
					className="report-stream__bar-completed"
					style={{ width: share(summary.completed) }}
				/>
				<span
					className="report-stream__bar-overdue"
					style={{ width: share(summary.overdue) }}
				/>
			</span>
			{summary.overdue > 0 && (
				<span className="report-stream__overdue">
					{`${summary.overdue} overdue`}
				</span>
			)}
		</>
	);
}

type ReportStreamRowMenuProps = {
	reportNumber?: unknown;
	// The report page's path.
	path: string;
	// Goes in the copied link, as the old right-click menu's did, so the link
	// opens the report in this tenant.
	tenantKey?: string;
	onExport: () => Promise<void>;
};

// A row's ⋯ menu: Open in a new tab, Copy link and Export PDF. The button
// spins while an export runs.
export function ReportStreamRowMenu({
	reportNumber,
	path,
	tenantKey,
	onExport,
}: Readonly<ReportStreamRowMenuProps>) {
	const [open, setOpen] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const number = typeof reportNumber === "string" ? reportNumber : "";

	const copyLink = async () => {
		const query = tenantKey
			? `?tenant_key=${encodeURIComponent(tenantKey)}`
			: "";
		try {
			await navigator.clipboard.writeText(
				`${window.location.origin}${path}${query}`
			);
			alertMessage("success", "Link copied to clipboard!");
		} catch (error) {
			console.error("Failed to copy the link:", error);
			alertMessage("error", "The link couldn't be copied.");
		}
	};

	const exportReport = async () => {
		setIsExporting(true);
		try {
			await onExport();
		} catch (error) {
			// The message only: a request error carries the bearer token.
			console.error(
				"Failed to export the report:",
				error instanceof Error ? error.message : error
			);
		} finally {
			setIsExporting(false);
		}
	};

	// The icons are decoration: each item is named by its label.
	const items: MenuProps["items"] = [
		{
			key: "open",
			icon: <ExportOutlined aria-hidden="true" />,
			label: "Open in a new tab",
		},
		{
			key: "copy",
			icon: <LinkOutlined aria-hidden="true" />,
			label: "Copy link",
		},
		{
			key: "export",
			icon: <DownloadOutlined aria-hidden="true" />,
			label: "Export PDF",
			disabled: isExporting,
		},
	];

	const onClick: MenuProps["onClick"] = ({ key }) => {
		setOpen(false);
		if (key === "open") window.open(path, "_blank", "noopener,noreferrer");
		else if (key === "copy") void copyLink();
		else void exportReport();
	};

	return (
		<Dropdown
			trigger={["click"]}
			open={open}
			onOpenChange={setOpen}
			placement="bottomRight"
			autoFocus
			menu={{ items, onClick }}
		>
			<Button
				type="text"
				className="report-stream__menu-button"
				icon={<EllipsisOutlined />}
				loading={isExporting}
				aria-label={number ? `More actions for ${number}` : "More actions"}
				aria-haspopup="menu"
				aria-expanded={open}
			/>
		</Dropdown>
	);
}
